import React, { useRef, useCallback } from "react";
import { X, Folder } from "lucide-react";

const useHorizontalScroll = () => {
  const ref = useRef(null);
  const onWheel = useCallback((e) => {
    if (!ref.current) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      ref.current.scrollLeft += e.deltaY;
    }
  }, []);
  return { ref, onWheel };
};

// Scrollbar hiding now handled by the .scrollbar-hide CSS class
// which covers webkit (Chrome/Android), Firefox, and IE/Edge

const FilterBar = ({
  projects,
  tags,
  activeProjectId,
  activeTagIds,
  songCounts,
  onProjectSelect,
  onTagToggle,
  onClearFilters,
  totalSongCount,
  isMobile,
}) => {
  const hasActiveFilters = activeProjectId !== null || activeTagIds.length > 0;
  const projectScroll = useHorizontalScroll();
  const tagScroll = useHorizontalScroll();

  const projectChips = (
    <>
      <button
        onClick={() => onProjectSelect(null)}
        className={`flex-shrink-0 text-xs px-3 py-1.5 min-h-[36px] md:min-h-0 rounded border transition-colors inline-flex items-center gap-1.5 ${
          activeProjectId === null
            ? "border-gold-500 text-gold-500"
            : "bg-forest-700 border-forest-600 text-forest-200 hover:border-forest-500"
        }`}
        style={
          activeProjectId === null
            ? { boxShadow: "0 0 12px rgba(197, 163, 78, 0.25)" }
            : {}
        }
      >
        All Songs
        <span className="text-forest-400">({totalSongCount || 0})</span>
      </button>

      <button
        onClick={() => onProjectSelect("unorganized")}
        className={`flex-shrink-0 text-xs px-3 py-1.5 min-h-[36px] md:min-h-0 rounded border transition-colors inline-flex items-center gap-1.5 ${
          activeProjectId === "unorganized"
            ? "border-gold-500 text-gold-500"
            : "bg-forest-700 border-forest-600 text-forest-200 hover:border-forest-500"
        }`}
        style={
          activeProjectId === "unorganized"
            ? { boxShadow: "0 0 12px rgba(197, 163, 78, 0.25)" }
            : {}
        }
      >
        Unorganized
        <span className="text-forest-400">({songCounts.unorganized || 0})</span>
      </button>

      {projects.map((project) => {
        const isActive = activeProjectId === project.id;
        const color = project.color || "#c5a34e";
        return (
          <button
            key={project.id}
            onClick={() => onProjectSelect(project.id)}
            className={`flex-shrink-0 text-xs px-3 py-1.5 min-h-[36px] md:min-h-0 rounded border transition-colors inline-flex items-center gap-1.5 ${
              isActive
                ? ""
                : "bg-forest-700 border-forest-600 text-forest-200 hover:border-forest-500"
            }`}
            style={
              isActive
                ? {
                    borderColor: color,
                    color: color,
                    boxShadow: `0 0 12px ${color}40`,
                  }
                : {}
            }
          >
            <Folder style={{ width: "14px", height: "14px", flexShrink: 0 }} />
            {project.color && (
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: project.color }}
              />
            )}
            {project.name}
            <span className="text-forest-400">
              ({songCounts[project.id] || 0})
            </span>
          </button>
        );
      })}
    </>
  );

  const tagChips = tags.map((tag) => {
    const isActive = activeTagIds.includes(tag.id);
    const color = tag.color || "#c5a34e";
    return (
      <button
        key={tag.id}
        onClick={() => onTagToggle(tag.id)}
        className={`flex-shrink-0 text-xs px-2.5 py-1 min-h-[32px] md:min-h-0 rounded-full border transition-colors inline-flex items-center gap-1 ${
          isActive
            ? ""
            : "bg-forest-800 border-forest-600 text-forest-300 hover:border-forest-500"
        }`}
        style={
          isActive
            ? {
                borderColor: color,
                color: color,
                boxShadow: `0 0 12px ${color}40`,
              }
            : {}
        }
      >
        {tag.color && (
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: tag.color }}
          />
        )}
        #{tag.name}
        <span className="text-forest-400">({songCounts[tag.id] || 0})</span>
      </button>
    );
  });

  // Desktop: side-by-side, each 50% width matching storage buttons
  if (!isMobile) {
    return (
      <div className="mb-4 space-y-2">
        <div className="flex gap-2">
          {/* Projects — left half (matches Local button width) */}
          <div
            ref={projectScroll.ref}
            onWheel={projectScroll.onWheel}
            className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide"
            style={{ flex: "1 1 0%", minWidth: 0 }}
          >
            {projectChips}
          </div>

          {/* Tags — right half (matches Cloud button width) */}
          {tags.length > 0 && (
            <div
              ref={tagScroll.ref}
              onWheel={tagScroll.onWheel}
              className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide"
              style={{ flex: "1 1 0%", minWidth: 0 }}
            >
              {tagChips}
            </div>
          )}
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-xs text-gold-500 hover:text-gold-400 inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear filters
          </button>
        )}
      </div>
    );
  }

  // Mobile: stacked rows (unchanged)
  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {projectChips}
      </div>

      {tags.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {tagChips}
        </div>
      )}

      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          className="text-xs text-gold-500 hover:text-gold-400 inline-flex items-center gap-1"
        >
          <X className="w-3 h-3" />
          Clear filters
        </button>
      )}
    </div>
  );
};

export default FilterBar;
